/**
 * Data room upload zone - drag-drop component for admin/team users
 * (CHAP-96).
 *
 * Click OR drag-drop one or more files. For each:
 *   - Validates content-type against allowlist (server re-validates)
 *   - Validates size (50MB inline cap; > 50MB shows "use Drive" message)
 *   - Per-file progress bar via XHR upload events
 *   - On complete: invokes the upload hook + invalidates the documents
 *     query so the list refreshes
 *
 * Sensitivity defaults to 'restricted' (server-side default per
 * dataroom_documents schema); admin can change in the per-file form.
 *
 * Per CHAP-96 ACs. Backend already shipped (POST /upload-token + POST
 * /documents); this is the client surface.
 */

import { useState, useRef, type DragEvent, type ChangeEvent } from 'react';
import { useUploadDocument } from '../../hooks/useDocuments.js';
import type { SensitivityTier } from '../../../shared/types.js';
import { ApiError } from '../../lib/api.js';

const MAX_INLINE_BYTES = 50 * 1024 * 1024; // 50 MB
const ACCEPT_LIST = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/png',
  'image/jpeg',
  'image/gif',
  'audio/mpeg',
  'audio/mp4',
  'video/mp4',
  'text/markdown',
  'text/plain',
  'text/csv',
];

interface QueuedFile {
  id: string; // local UUID for React key
  file: File;
  sensitivity: SensitivityTier;
  description: string;
  status: 'queued' | 'uploading' | 'done' | 'failed';
  bytesUploaded: number;
  error?: string;
}

function newId(): string {
  return Math.random().toString(36).slice(2);
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

interface UploadZoneProps {
  slug: string;
}

export default function UploadZone({ slug }: UploadZoneProps) {
  const [files, setFiles] = useState<QueuedFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadDocument(slug);

  function addFiles(incoming: FileList | File[]) {
    const queued: QueuedFile[] = [];
    for (const file of Array.from(incoming)) {
      // Pre-flight checks
      const contentType = file.type || 'application/octet-stream';
      if (file.size > MAX_INLINE_BYTES) {
        queued.push({
          id: newId(),
          file,
          sensitivity: 'restricted',
          description: '',
          status: 'failed',
          bytesUploaded: 0,
          error: `Too large (${formatBytes(file.size)}). Files >50MB go to Google Drive - paste a Drive link in the document description instead.`,
        });
        continue;
      }
      if (!ACCEPT_LIST.includes(contentType)) {
        queued.push({
          id: newId(),
          file,
          sensitivity: 'restricted',
          description: '',
          status: 'failed',
          bytesUploaded: 0,
          error: `Content-Type '${contentType}' is not in the allowed list. Allowed: pdf, docx, xlsx, pptx, png, jpg, gif, mp3, mp4, m4a, md, txt, csv.`,
        });
        continue;
      }
      queued.push({
        id: newId(),
        file,
        sensitivity: 'restricted',
        description: '',
        status: 'queued',
        bytesUploaded: 0,
      });
    }
    setFiles((prev) => [...prev, ...queued]);
  }

  function updateFile(id: string, patch: Partial<QueuedFile>) {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  }

  async function startUpload(id: string) {
    const target = files.find((f) => f.id === id);
    if (!target || target.status !== 'queued') return;

    updateFile(id, { status: 'uploading', bytesUploaded: 0 });

    try {
      await upload.mutateAsync({
        file: target.file,
        sensitivityTier: target.sensitivity,
        description: target.description || undefined,
        onProgress: (bytes) => updateFile(id, { bytesUploaded: bytes }),
      });
      updateFile(id, {
        status: 'done',
        bytesUploaded: target.file.size,
      });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.status}: ${
              typeof err.body === 'object' && err.body !== null && 'message' in err.body
                ? (err.body as { message: string }).message
                : err.message
            }`
          : err instanceof Error
            ? err.message
            : String(err);
      updateFile(id, { status: 'failed', error: msg });
    }
  }

  function uploadAll() {
    for (const f of files) {
      if (f.status === 'queued') void startUpload(f.id);
    }
  }

  function removeFile(id: string) {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  }

  function onDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(true);
  }

  function onDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
  }

  function onFileInput(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = ''; // reset so same file can be re-selected
    }
  }

  const queuedCount = files.filter((f) => f.status === 'queued').length;

  return (
    <div className="space-y-3 min-w-0">
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 sm:p-10 text-center cursor-pointer transition-colors ${
          dragOver
            ? 'border-navy bg-navy/5'
            : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
        }`}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
      >
        <p className="text-navy font-medium">
          Drag files here, or click to choose
        </p>
        <p className="text-xs text-neutral mt-1">
          Up to 50MB per file. Larger files go to Google Drive - paste the link in
          the document description instead.
        </p>
        <p className="text-xs text-neutral mt-1">
          Allowed: pdf, docx, xlsx, pptx, png, jpg, gif, mp3, mp4, m4a, md, txt, csv
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          accept={ACCEPT_LIST.join(',')}
          onChange={onFileInput}
        />
      </div>

      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((f) => (
            <FileRow
              key={f.id}
              file={f}
              onUpdate={(patch) => updateFile(f.id, patch)}
              onUpload={() => startUpload(f.id)}
              onRemove={() => removeFile(f.id)}
            />
          ))}
          {queuedCount > 1 && (
            <button
              type="button"
              onClick={uploadAll}
              className="bg-navy text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-navy/90"
            >
              Upload all ({queuedCount})
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface FileRowProps {
  file: QueuedFile;
  onUpdate: (patch: Partial<QueuedFile>) => void;
  onUpload: () => void;
  onRemove: () => void;
}

function FileRow({ file, onUpdate, onUpload, onRemove }: FileRowProps) {
  const pct =
    file.file.size > 0 ? Math.round((file.bytesUploaded / file.file.size) * 100) : 0;

  return (
    <div className="border border-gray-200 rounded-md p-3 bg-white min-w-0">
      <div className="flex items-start justify-between gap-3 min-w-0">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-navy text-sm truncate">{file.file.name}</div>
          <div className="text-xs text-neutral mt-0.5">
            {formatBytes(file.file.size)} · {file.file.type || 'application/octet-stream'}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {file.status === 'queued' && (
            <>
              <select
                value={file.sensitivity}
                onChange={(e) =>
                  onUpdate({ sensitivity: e.target.value as SensitivityTier })
                }
                className="text-xs border border-gray-300 rounded px-2 py-1"
                aria-label="Sensitivity tier"
              >
                <option value="public">Public</option>
                <option value="restricted">Restricted</option>
                <option value="confidential">Confidential</option>
                <option value="highly_confidential">Highly confidential</option>
              </select>
              <button
                type="button"
                onClick={onUpload}
                className="bg-navy text-white text-xs font-medium px-3 py-1 rounded hover:bg-navy/90"
              >
                Upload
              </button>
              <button
                type="button"
                onClick={onRemove}
                className="text-neutral hover:text-red-600 text-xs px-1"
                aria-label="Remove from queue"
              >
                ✕
              </button>
            </>
          )}
          {file.status === 'uploading' && (
            <span className="text-xs text-neutral">{pct}%</span>
          )}
          {file.status === 'done' && (
            <span className="text-xs text-green-700 font-medium">✓ Uploaded</span>
          )}
          {file.status === 'failed' && (
            <button
              type="button"
              onClick={onRemove}
              className="text-xs text-red-700 hover:underline"
            >
              Remove
            </button>
          )}
        </div>
      </div>
      {file.status === 'queued' && (
        <input
          type="text"
          placeholder="Description (optional)"
          value={file.description}
          onChange={(e) => onUpdate({ description: e.target.value })}
          className="mt-2 w-full text-xs border border-gray-200 rounded px-2 py-1"
        />
      )}
      {file.status === 'uploading' && (
        <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-navy transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      {file.status === 'failed' && file.error && (
        <p className="text-xs text-red-700 mt-1">{file.error}</p>
      )}
    </div>
  );
}
